/**
 * ⟦C57 · lot 1 · A-174⟧ **Le bouton « Les dessous » d'une entrée du Journal**, prêt à poser.
 *
 * Une entrée du Journal garde une ligne courte ; quand le fait qu'elle raconte a un détail
 * (`JournalEntry.detail`, écrit par `game/journal.ts`), ce composant le rouvre dans le dialogue
 * partagé `LesDessous` : même fenêtre, même clavier (Échap ferme ce dialogue seul, le focus
 * revient au bouton), même dessin que partout ailleurs dans le jeu.
 *
 * Le détail est une DONNÉE (`DetailJournal`, du JSON pur qui a survécu au rechargement) : c'est
 * ici qu'il est mis en page, à l'ouverture. Un seul endroit, donc le Journal, une fenêtre qui
 * raconte le même fait, ou les faits d'un récap affichent le même détail de la même façon :
 *
 * ```tsx
 * {entree.detail ? <DessousDuJournal detail={entree.detail} /> : null}
 * ```
 *
 * Par défaut le pictogramme « ? » seul (`variante="icone"`) : une liste de trente entrées n'a pas à
 * répéter « Les dessous » trente fois, ce serait précisément du texte en trop. Le nom accessible
 * reste « Les dessous : <sujet> ».
 */

import { useRef, useState, type ReactNode } from "react";

import type { DetailJournal } from "../game/gameClient";

import { DessousBloc, DessousLignes, DialogueDessous, LesDessous } from "./LesDessous";

/** Le détail d'un fait, mis en page : un bloc par bloc du détail. Un seul dessin pour tous les écrans. */
function BlocsDuDetail({ detail }: { detail: DetailJournal }) {
  return (
    <>
      {detail.blocs.map((bloc, i) => (
        <DessousBloc key={i} titre={bloc.titre}>
          {(bloc.paragraphes ?? []).map((p, j) => (
            <p className="dessous-texte" key={j}>
              {p}
            </p>
          ))}
          {bloc.lignes && bloc.lignes.length > 0 ? <DessousLignes lignes={bloc.lignes} /> : null}
        </DessousBloc>
      ))}
    </>
  );
}

export interface DessousDuJournalProps {
  detail: DetailJournal;
  /** `icone` (défaut, pour une liste) ou `texte` (« ? Les dessous », pour un fait seul). */
  variante?: "texte" | "icone";
  className?: string;
}

export function DessousDuJournal({ detail, variante = "icone", className }: DessousDuJournalProps) {
  return (
    <LesDessous titre={detail.titre} variante={variante} className={className}>
      <BlocsDuDetail detail={detail} />
    </LesDessous>
  );
}

/**
 * ⟦C62 · lot arbre · A-179⟧ **Une ligne de liste qui ouvre son détail en cliquant la ligne**, sans « ? » par
 * ligne : la carte « Notifications » de l'écran principal garde jusqu'à dix lignes, chacune avec un « ? » avant ;
 * c'est exactement ce que Matt a vu dans l'Arbre (un bouton par nœud). Le contenu de la ligne est son texte ; un
 * « ▸ » discret dit qu'elle s'ouvre (le même que « Journal ▸ »). Même dialogue, même clavier, même détail.
 */
export function LigneAvecDetail({
  detail,
  className,
  children,
}: {
  detail: DetailJournal;
  className?: string;
  children: ReactNode;
}) {
  const [ouvert, setOuvert] = useState(false);
  const declencheur = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button
        ref={declencheur}
        type="button"
        className={`ligne-avec-detail${className ? ` ${className}` : ""}`}
        aria-haspopup="dialog"
        aria-expanded={ouvert}
        onClick={() => setOuvert(true)}
      >
        <span className="ligne-avec-detail-texte">{children}</span>
        <span className="ligne-avec-detail-suite" aria-hidden="true">
          ▸
        </span>
      </button>
      {ouvert ? (
        <DialogueDessous titre={detail.titre} onFermer={() => setOuvert(false)} retour={declencheur}>
          <BlocsDuDetail detail={detail} />
        </DialogueDessous>
      ) : null}
    </>
  );
}
